/**
 * @module composables/useDashboardFilter
 * @description 대시보드 기간/파트너 필터 상태를 관리하는 composable.
 * 기간 프리셋(TODAY, WEEK, MONTH, YEAR) 선택 또는 사용자 지정 날짜 범위를 지원하며,
 * 파트너 필터링 기능을 제공합니다.
 */
import { ref, computed } from 'vue'

// ── 기간 프리셋 상수 (Period Preset Constants) ──

/** 오늘 */
const PERIOD_TODAY = 'TODAY'
/** 이번 주 */
const PERIOD_WEEK = 'WEEK'
/** 이번 달 */
const PERIOD_MONTH = 'MONTH'
/** 올해 */
const PERIOD_YEAR = 'YEAR'

/** 사용 가능한 기간 프리셋 목록 */
const VALID_PERIODS = [PERIOD_TODAY, PERIOD_WEEK, PERIOD_MONTH, PERIOD_YEAR]

/**
 * 대시보드 필터 상태를 관리하는 composable입니다.
 * 기간 프리셋 선택, 사용자 지정 날짜 범위 입력, 파트너 필터링을 지원합니다.
 *
 * - periodStatus: 기간 프리셋 (TODAY | WEEK | MONTH | YEAR)
 * - fromDate: 사용자 지정 시작 날짜 (Date | null)
 * - toDate: 사용자 지정 종료 날짜 (Date | null)
 * - partnerId: 선택된 파트너 ID (number | null)
 * - isCustomDateRange: 사용자 지정 날짜 범위 사용 여부
 *
 * @returns {{
 *   periodStatus: import('vue').Ref<string>,
 *   fromDate: import('vue').Ref<Date|null>,
 *   toDate: import('vue').Ref<Date|null>,
 *   partnerId: import('vue').Ref<number|null>,
 *   isCustomDateRange: import('vue').ComputedRef<boolean>,
 *   changePeriod: (period: string) => void,
 *   setDateRange: (from: Date, to: Date) => void,
 *   resetFilter: () => void
 * }}
 */
export function useDashboardFilter() {
  // ── 상태 (State) ──

  /** 기간 프리셋 상태 — 진입 시 기본값은 MONTH(최근 한달). */
  const periodStatus = ref(PERIOD_MONTH)

  /** 사용자 지정 시작 날짜 */
  const fromDate = ref(null)

  /** 사용자 지정 종료 날짜 */
  const toDate = ref(null)

  /** 선택된 파트너 ID */
  const partnerId = ref(null)

  // ── 계산된 속성 (Computed) ──

  /**
   * 사용자 지정 날짜 범위가 설정되었는지 여부를 반환합니다.
   * fromDate와 toDate가 모두 존재할 때 true를 반환합니다.
   *
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isCustomDateRange = computed(() => {
    return fromDate.value !== null && toDate.value !== null
  })

  // ── 메서드 (Methods) ──

  /**
   * 기간 프리셋을 변경하고 사용자 지정 날짜 범위를 초기화합니다.
   * 유효하지 않은 기간 값이 전달되면 변경하지 않습니다.
   *
   * @param {string} period - 기간 프리셋 값 (TODAY | WEEK | MONTH | YEAR)
   * @returns {void}
   */
  const changePeriod = (period) => {
    if (!VALID_PERIODS.includes(period)) {
      return
    }
    periodStatus.value = period
    fromDate.value = null
    toDate.value = null
  }

  /**
   * 사용자 지정 날짜 범위를 설정합니다.
   * from과 to가 모두 유효한 값이어야 설정됩니다.
   *
   * @param {Date} from - 시작 날짜
   * @param {Date} to - 종료 날짜
   * @returns {void}
   */
  const setDateRange = (from, to) => {
    if (!from || !to) {
      return
    }
    fromDate.value = from
    toDate.value = to
  }

  /**
   * 모든 필터 상태를 초기값으로 리셋합니다.
   * periodStatus는 TODAY로, 나머지는 null로 초기화됩니다.
   *
   * @returns {void}
   */
  const resetFilter = () => {
    periodStatus.value = PERIOD_MONTH
    fromDate.value = null
    toDate.value = null
    partnerId.value = null
  }

  return {
    periodStatus,
    fromDate,
    toDate,
    partnerId,
    isCustomDateRange,
    changePeriod,
    setDateRange,
    resetFilter
  }
}
